export function padelMaxGenerateGames(confirmedCount: number): number {
  if (confirmedCount < 4) return 0;
  return Math.floor((confirmedCount * (confirmedCount - 1)) / 4);
}

export function padelAbsoluteMaxGenerateGames(confirmedCount: number): number {
  if (confirmedCount < 4) return 0;
  return Math.floor((confirmedCount * (confirmedCount - 1)) / 2);
}

export function padelSparringMaxGenerateGames(countA: number, countB: number): number {
  if (countA < 2 || countB < 2) return 0;
  const pairsA = (countA * (countA - 1)) / 2;
  const pairsB = (countB * (countB - 1)) / 2;
  return Math.min(pairsA, pairsB);
}

export function padelSparringAbsoluteMaxGenerateGames(countA: number, countB: number): number {
  if (countA < 2 || countB < 2) return 0;
  const pairsA = (countA * (countA - 1)) / 2;
  const pairsB = (countB * (countB - 1)) / 2;
  return pairsA * pairsB;
}

export type PadelScoringKind = 'americano' | 'best_of';
export type PadelRotationFormat = 'americano' | 'round_robin' | 'mexicano';
export type PadelTargetScore = 16 | 21 | 24 | 32;
export type PadelMaxSets = 1 | 3;

export interface TambahPadelGameFormState {
  scoringKind: PadelScoringKind;
  targetScore: PadelTargetScore;
  maxSets: PadelMaxSets;
  numCourts: number;
  rotationFormat: PadelRotationFormat;
  teams: number[][];
}

export interface PadelGeneratePayload {
  rotationFormat: PadelRotationFormat;
  numCourts: number;
  scoringType?: 'points' | 'sets';
  targetScore?: PadelTargetScore;
  maxSets?: PadelMaxSets;
  teams?: number[][];
}

export interface AmericanoSchedulePreview {
  totalGames: number;
  parallel: number;
  rounds: number;
}

export function createDefaultTambahPadelGameForm(): TambahPadelGameFormState {
  return {
    scoringKind: 'americano',
    targetScore: 21,
    maxSets: 1,
    numCourts: 2,
    rotationFormat: 'americano',
    teams: [],
  };
}

export function previewAmericanoSchedule(
  playerCount: number,
  numCourts: number
): AmericanoSchedulePreview {
  const totalGames = playerCount < 4 ? 0 : Math.floor((playerCount * (playerCount - 1)) / 4);
  const parallel = Math.min(Math.max(0, numCourts), Math.max(0, Math.floor(playerCount / 4)));
  const rounds = parallel > 0 ? Math.ceil(totalGames / parallel) : 0;
  return { totalGames, parallel, rounds };
}

export function americanoPreviewText(playerCount: number, numCourts: number): string {
  const { totalGames, rounds } = previewAmericanoSchedule(playerCount, numCourts);
  return `Akan menghasilkan ${totalGames} pertandingan · ${rounds} ronde`;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

export function previewMexicanoSchedule(
  playerCount: number,
  _numCourts?: number
): { courts: number; byes: number; minRounds: number } {
  const courts = Math.max(1, Math.floor(playerCount / 4));
  const byes = Math.max(0, playerCount - courts * 4);
  let minRounds = 3;
  if (byes > 0) {
    const g = gcd(playerCount, byes);
    minRounds = Math.floor(playerCount / g);
  }
  return { courts, byes, minRounds };
}

export function mexicanoPreviewText(playerCount: number, numCourts?: number): string {
  if (playerCount < 4) return 'Butuh minimal 4 pemain';
  const { courts, byes, minRounds } = previewMexicanoSchedule(playerCount, numCourts);
  const byeText = byes > 0 ? `${byes} pemain istirahat/ronde (saran kelipatan ${minRounds} ronde)` : 'tanpa istirahat';
  return `Ronde 1: ${courts} pertandingan (${courts} lapangan) · ${byeText}`;
}

function isTeamComplete(team: number[]): boolean {
  return team.length === 2 && team.every((id) => id > 0);
}

export function completePadelTeamCount(teams: number[][]): number {
  return teams.filter(isTeamComplete).length;
}

export function applyScoringKind(
  form: TambahPadelGameFormState,
  scoringKind: PadelScoringKind
): TambahPadelGameFormState {
  return {
    ...form,
    scoringKind,
  };
}

export function applyRotationFormat(
  form: TambahPadelGameFormState,
  rotationFormat: PadelRotationFormat
): TambahPadelGameFormState {
  return {
    ...form,
    rotationFormat,
  };
}

export function isTambahPadelGameSubmitEnabled(
  form: TambahPadelGameFormState,
  confirmedCount: number
): boolean {
  if (form.rotationFormat === 'round_robin') {
    return completePadelTeamCount(form.teams) >= 2;
  }
  return confirmedCount >= 4;
}

export function buildGeneratePayload(form: TambahPadelGameFormState): PadelGeneratePayload {
  const isBestOf = form.scoringKind === 'best_of';
  const payload: PadelGeneratePayload = {
    rotationFormat: form.rotationFormat,
    numCourts: form.numCourts,
    scoringType: isBestOf ? 'sets' : 'points',
  };
  if (isBestOf) {
    payload.maxSets = form.maxSets;
  } else {
    payload.targetScore = form.targetScore;
  }
  if (form.rotationFormat === 'round_robin') {
    payload.teams = form.teams;
  }
  return payload;
}

export function generateSuccessCopy(
  form: TambahPadelGameFormState,
  confirmedCount: number
): { title: string; message: string } {
  const scoringText =
    form.scoringKind === 'best_of'
      ? `Best of ${form.maxSets}`
      : `${form.targetScore} poin`;

  if (form.rotationFormat === 'round_robin') {
    const pairs = completePadelTeamCount(form.teams);
    const games = (pairs * (pairs - 1)) / 2;
    return {
      title: 'Jadwal Dibuat · Round Robin',
      message: `${pairs} pasangan dibuat, menghasilkan ${games} laga. Klasemen dihitung dari hasil set. Format Ganda · ${scoringText} · ${form.numCourts} lapangan.`,
    };
  }
  if (form.rotationFormat === 'mexicano') {
    const preview = previewMexicanoSchedule(confirmedCount, form.numCourts);
    const byeMsg = preview.byes > 0 ? ` (${preview.byes} istirahat)` : '';
    return {
      title: 'Jadwal Dibuat · Mexicano Ronde 1',
      message: `${confirmedCount} pemain · Ronde 1: ${preview.courts} pertandingan (${preview.courts} lapangan) diundi acak${byeMsg}. Format Ganda · ${scoringText}. Ronde berikutnya di-generate setelah skor ronde selesai.`,
    };
  }
  const preview = previewAmericanoSchedule(confirmedCount, form.numCourts);
  return {
    title: 'Jadwal Dibuat · Americano',
    message: `${confirmedCount} pemain, menghasilkan ${preview.totalGames} laga · ${preview.rounds} ronde. Format Ganda · Americano ${scoringText} · ${form.numCourts} lapangan.`,
  };
}
